import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { getToken, setToken, clearToken } from "@/lib/authToken";
import { logError } from "@/lib/logger";

// Manages the logged-in user + JWT. On mount, if a token exists it validates it
// via /auth/me. Exposes login/register/logout. This is a separate layer from the
// ephemeral CRM session; it never changes HubSpot read-only behavior.
export function useAuth() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false); // finished initial token check

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getToken();
      if (!token) {
        setReady(true);
        return;
      }
      try {
        const { user: me } = await api.me();
        if (!cancelled) setUser(me);
      } catch (e) {
        // Invalid/expired token — clear it silently.
        clearToken();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { token, user: u } = await api.login(email, password);
    setToken(token);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password) => {
    const { token, user: u } = await api.register(email, password);
    setToken(token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    try {
      clearToken();
    } catch (e) {
      logError("logout failed:", e);
    }
    setUser(null);
  }, []);

  return { user, ready, login, register, logout, isAuthenticated: !!user, isAdmin: user?.role === "admin" };
}
